import React, { useState, useEffect, useCallback } from 'react';
import { 
  PhotoIcon,
  VideoCameraIcon,
  ShareIcon,
  HashtagIcon,
  DocumentIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PlayIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import MediaGallery from '../../components/media/MediaGallery';
import FeaturedMedia from '../../components/media/FeaturedMedia';
import SocialMediaFeed from '../../components/media/SocialMediaFeed';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const MediaPage = () => {
  const [activeTab, setActiveTab] = useState('gallery');

  // Sample media data (will be replaced by API data when available)
  const sampleMedia = [
    {
      id: 1,
      filename: 'championship-game-highlights.jpg',
      originalName: 'Championship Game Highlights',
      mimeType: 'image/jpeg',
      size: 2048576,
      path: '/uploads/media/championship-game-highlights.jpg',
      url: '/api/placeholder/800/600',
      mediaType: 'image',
      category: 'game-photos',
      title: 'Championship Game Victory',
      description: 'Eagles celebrating their championship win against Central Valley Hawks',
      altText: 'Football players celebrating championship victory',
      uploadedBy: {
        id: 1,
        firstName: 'Sarah',
        lastName: 'Johnson'
      },
      sport: {
        id: 1,
        name: 'Football',
        slug: 'football'
      },
      isPublic: true,
      downloadCount: 45,
      tags: ['championship', 'football', 'victory', 'celebration'],
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: 2,
      filename: 'basketball-practice-video.mp4',
      originalName: 'Basketball Practice Session',
      mimeType: 'video/mp4',
      size: 15728640,
      path: '/uploads/media/basketball-practice-video.mp4',
      url: '/api/placeholder/800/600',
      mediaType: 'video',
      category: 'practice-videos',
      title: 'Girls Basketball Practice Highlights',
      description: 'Training session focusing on defensive strategies and teamwork',
      altText: 'Girls basketball team practicing defensive drills',
      uploadedBy: {
        id: 2,
        firstName: 'Coach',
        lastName: 'Thompson'
      },
      sport: {
        id: 4,
        name: 'Girls Basketball',
        slug: 'girls-basketball'
      },
      isPublic: true,
      downloadCount: 23,
      tags: ['basketball', 'practice', 'training', 'defense'],
      createdAt: '2024-01-12T14:00:00Z'
    },
    {
      id: 3,
      filename: 'athletic-handbook-2024.pdf',
      originalName: 'Athletic Handbook 2024',
      mimeType: 'application/pdf',
      size: 1048576,
      path: '/uploads/media/athletic-handbook-2024.pdf',
      url: '/api/placeholder/400/500',
      mediaType: 'document',
      category: 'handbooks',
      title: '2024 Athletic Handbook',
      description: 'Complete guide to East End Prep athletics policies and procedures',
      altText: 'Athletic handbook cover page',
      uploadedBy: {
        id: 3,
        firstName: 'Athletic',
        lastName: 'Director'
      },
      sport: null,
      isPublic: true,
      downloadCount: 156,
      tags: ['handbook', 'policies', 'procedures', 'athletics'],
      createdAt: '2024-01-01T09:00:00Z'
    },
    {
      id: 4,
      filename: 'soccer-team-photo.jpg',
      originalName: 'Boys Soccer Team Photo',
      mimeType: 'image/jpeg',
      size: 3145728,
      path: '/uploads/media/soccer-team-photo.jpg',
      url: '/api/placeholder/800/600',
      mediaType: 'image',
      category: 'team-photos',
      title: 'Boys Soccer Team 2024',
      description: 'Official team photo for the 2024 boys soccer season',
      altText: 'Boys soccer team posing for official team photo',
      uploadedBy: {
        id: 4,
        firstName: 'Coach',
        lastName: 'Rodriguez'
      },
      sport: {
        id: 3,
        name: 'Boys Soccer',
        slug: 'boys-soccer'
      },
      isPublic: true,
      downloadCount: 78,
      tags: ['soccer', 'team-photo', 'boys', '2024'],
      createdAt: '2024-01-08T11:00:00Z'
    },
    {
      id: 5,
      filename: 'track-meet-highlights.jpg',
      originalName: 'Track Meet Action Shots',
      mimeType: 'image/jpeg',
      size: 2621440,
      path: '/uploads/media/track-meet-highlights.jpg',
      url: '/api/placeholder/800/600',
      mediaType: 'image',
      category: 'game-photos',
      title: 'Track & Field Meet Highlights',
      description: 'Action shots from the regional track and field championship',
      altText: 'Athletes competing in track and field events',
      uploadedBy: {
        id: 5,
        firstName: 'Coach',
        lastName: 'Davis'
      },
      sport: {
        id: 6,
        name: 'Track & Field',
        slug: 'track-field'
      },
      isPublic: true,
      downloadCount: 34,
      tags: ['track', 'field', 'championship', 'athletics'],
      createdAt: '2024-01-05T16:30:00Z'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'game-photos', label: 'Game Photos' },
    { value: 'team-photos', label: 'Team Photos' },
    { value: 'practice-videos', label: 'Practice Videos' },
    { value: 'game-videos', label: 'Game Videos' },
    { value: 'handbooks', label: 'Handbooks' },
    { value: 'forms', label: 'Forms' },
    { value: 'schedules', label: 'Schedules' }
  ];

  const mediaTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'image', label: 'Images' },
    { value: 'video', label: 'Videos' },
    { value: 'document', label: 'Documents' }
  ];

  const sports = [
    { value: 'all', label: 'All Sports' },
    { value: 'football', label: 'Football' },
    { value: 'girls-basketball', label: 'Girls Basketball' },
    { value: 'boys-basketball', label: 'Boys Basketball' },
    { value: 'girls-soccer', label: 'Girls Soccer' },
    { value: 'boys-soccer', label: 'Boys Soccer' },
    { value: 'track-field', label: 'Track & Field' }
  ];

  // State variables
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching media from API...');
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 12,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(selectedType !== 'all' && { type: selectedType }),
        ...(selectedSport !== 'all' && { sport: selectedSport })
      });

      const url = `${API_BASE_URL}/media?${params}`;
      console.log('API URL:', url);
      const response = await fetch(url);
      console.log('API Response status:', response.status);
      
      const responseText = await response.text();
      
      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          console.log('API Response data:', data);
          console.log('Setting media data:', data.media);
          setMedia(data.media || []);
          setTotalPages(data.pagination?.totalPages || 1);
        } catch (parseError) {
          console.error('Error parsing JSON response:', parseError);
          console.log('Response text:', responseText);
          setMedia([]);
          setTotalPages(1);
        }
      } else {
        console.log('API call failed with status:', response.status);
        console.log('Response text:', responseText);
        setMedia([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      setMedia([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedCategory, selectedType, selectedSport]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    if (typeof bytes !== 'number' || isNaN(bytes)) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMediaTypeIcon = (type) => {
    switch (type) {
      case 'image': return PhotoIcon;
      case 'video': return VideoCameraIcon;
      case 'document': return DocumentIcon;
      default: return DocumentIcon;
    }
  };

  const getMediaTypeColor = (type) => {
    switch (type) {
      case 'image': return 'bg-green-100 text-green-800';
      case 'video': return 'bg-blue-100 text-blue-800';
      case 'document': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openLightbox = (mediaItem, index) => {
    if (mediaItem.mediaType === 'image' || mediaItem.mediaType === 'video') {
      setLightboxMedia(mediaItem);
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxMedia(null);
  };

  const navigateLightbox = (direction) => {
    const imageVideos = media.filter(item => item.mediaType === 'image' || item.mediaType === 'video');
    let newIndex = lightboxIndex + direction;
    
    if (newIndex < 0) newIndex = imageVideos.length - 1;
    if (newIndex >= imageVideos.length) newIndex = 0;
    
    setLightboxIndex(newIndex);
    setLightboxMedia(imageVideos[newIndex]);
  };

  const handleDownload = async (mediaItem) => {
    try {
      // In a real app, this would trigger a download and increment the download count
      const link = document.createElement('a');
      link.href = mediaItem.url;
      link.download = mediaItem.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };



  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMedia();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-lg text-white mb-4">Media Gallery</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Explore photos, videos, and documents from East End Prep athletics. 
            Relive the excitement and stay informed with our media collection.
          </p>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search media..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
            </form>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input text-sm"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>

              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input text-sm"
              >
                {mediaTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <select
                value={selectedSport}
                onChange={(e) => {
                  setSelectedSport(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input text-sm"
              >
                {sports.map(sport => (
                  <option key={sport.value} value={sport.value}>
                    {sport.label}
                  </option>
                ))}
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <div className="flex rounded-md border border-gray-300">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 text-sm font-medium rounded-l-md ${
                    viewMode === 'grid'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 text-sm font-medium rounded-r-md border-l ${
                    viewMode === 'list'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  List
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Showing {media.length} items
          </div>
        </div>
      </section>

      {/* Media Grid/List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner spinner-primary w-8 h-8"></div>
            </div>
          ) : media.length > 0 ? (
            <>
              {viewMode === 'grid' ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {media.map((item, index) => {
                    const IconComponent = getMediaTypeIcon(item.mediaType);
                    return (
                      <div key={item.id} className="card card-hover">
                        <div className="relative aspect-w-16 aspect-h-12">
                          {item.mediaType === 'image' ? (
                            <img
                              src={item.signedUrl || item.url}
                              alt={item.altText}
                              className="w-full h-48 object-cover cursor-pointer"
                              onClick={() => openLightbox(item, index)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : item.mediaType === 'video' ? (
                            <div 
                              className="w-full h-48 bg-gray-200 flex items-center justify-center cursor-pointer relative"
                              onClick={() => openLightbox(item, index)}
                            >
                              <PlayIcon className="w-16 h-16 text-white bg-black bg-opacity-50 rounded-full p-4" />
                              <img
                                src={item.signedUrl || item.url}
                                alt={item.altText}
                                className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                              <IconComponent className="w-16 h-16 text-gray-400" />
                            </div>
                          )}
                          
                          <div className="absolute top-2 left-2">
                            <span className={`badge ${getMediaTypeColor(item.mediaType)}`}>
                              {item.mediaType}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                            {item.title}
                          </h3>
                          
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {item.description}
                          </p>
                          
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                            <span>{formatDate(item.createdAt)}</span>
                            <span>{formatFileSize(item.fileSize || item.size)}</span>
                          </div>
                          
                          {item.sport && (
                            <div className="mb-3">
                              <span className="badge bg-primary text-white text-xs">
                                {item.sport.name}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-xs text-gray-500">
                              <EyeIcon className="w-3 h-3 mr-1" />
                              {item.downloadCount}
                            </span>
                            
                            <button
                              onClick={() => handleDownload(item)}
                              className="btn-primary text-xs px-3 py-1 flex items-center"
                            >
                              <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  {media.map((item, index) => {
                    const IconComponent = getMediaTypeIcon(item.mediaType);
                    return (
                      <div key={item.id} className="card">
                        <div className="p-6">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              {item.mediaType === 'image' ? (
                                <img
                                  src={item.signedUrl || item.url}
                                  alt={item.altText}
                                  className="w-20 h-20 object-cover rounded cursor-pointer"
                                  onClick={() => openLightbox(item, index)}
                                  onError={(e) => {
                                    e.target.src = '/api/placeholder/80/80';
                                  }}
                                />
                              ) : (
                                <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                                  <IconComponent className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-2">
                                <h3 className="font-bold text-gray-900">
                                  {item.title}
                                </h3>
                                <span className={`badge ${getMediaTypeColor(item.mediaType)}`}>
                                  {item.mediaType}
                                </span>
                                {item.sport && (
                                  <span className="badge bg-primary text-white">
                                    {item.sport.name}
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-gray-600 mb-3">
                                {item.description}
                              </p>
                              
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>Uploaded {formatDate(item.createdAt)}</span>
                                <span>{formatFileSize(item.fileSize || item.size)}</span>
                                <span className="flex items-center">
                                  <EyeIcon className="w-4 h-4 mr-1" />
                                  {item.downloadCount} downloads
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex-shrink-0">
                              <button
                                onClick={() => handleDownload(item)}
                                className="btn-primary flex items-center"
                              >
                                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-12">
                  <nav className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-md border ${
                          currentPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <PhotoIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Media Found</h3>
              <p className="text-gray-500">Try adjusting your search terms or filters to find more media.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative max-w-4xl max-h-full p-4">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            
            <button
              onClick={() => navigateLightbox(-1)}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            
            <button
              onClick={() => navigateLightbox(1)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
            
            {lightboxMedia.mediaType === 'image' ? (
              <img
                src={lightboxMedia.signedUrl || lightboxMedia.url}
                alt={lightboxMedia.altText}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.src = '/api/placeholder/800/600';
                }}
              />
            ) : (
              <video
                src={lightboxMedia.signedUrl || lightboxMedia.url}
                controls
                className="max-w-full max-h-full"
              >
                Your browser does not support the video tag.
              </video>
            )}
            
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded">
              <h3 className="font-bold mb-1">{lightboxMedia.title}</h3>
              <p className="text-sm text-gray-300">{lightboxMedia.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaPage;